/**
 * AP-39 8.3 — **Ziel suchen und zuordnen**: Vorschläge (Belege des erkannten Partners, passende Art
 * zuerst) und eine Suche über Belegnummer, Partner und Projekt. Im Seitenfeld für einen Eintrag,
 * im Dialog für mehrere.
 */
import { useEffect, useRef, useState } from 'react'
import { Link2, Loader2, Search } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { fetchTargetKinds, fetchTargets } from '../api/inbox.api'
import type { InboxTarget, InboxTargetKind } from '../types/inbox.types'

export function ZielSuche({ itemId, onWahl, besetzt = [] }: { itemId?: string; onWahl: (z: InboxTarget) => Promise<void>; besetzt?: string[] }) {
  const [arten, setArten] = useState<InboxTargetKind[]>([])
  const [art, setArt] = useState('')
  const [q, setQ] = useState('')
  const [treffer, setTreffer] = useState<InboxTarget[] | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const uhr = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    fetchTargetKinds()
      .then(setArten)
      .catch(() => setArten([]))
  }, [])

  useEffect(() => {
    if (uhr.current) clearTimeout(uhr.current)
    uhr.current = setTimeout(() => {
      fetchTargets({ q: q.trim() || undefined, kind: art || undefined, itemId })
        .then(setTreffer)
        .catch((e) => setFehler(apiErrorText(e, 'Die Suche hat nicht geklappt.')))
    }, 300)
    return () => {
      if (uhr.current) clearTimeout(uhr.current)
    }
  }, [q, art, itemId])

  const artLabel = (k: string) => arten.find((a) => a.kind === k)?.label ?? k
  const waehle = async (z: InboxTarget) => {
    setFehler(null)
    setLaeuft(z.targetKind + z.targetId)
    try {
      await onWahl(z)
    } catch (e) {
      setFehler(apiErrorText(e, 'Zuordnen hat nicht geklappt.'))
    } finally {
      setLaeuft(null)
    }
  }

  if (!arten.length) return <p className="text-xs text-muted-foreground">Kein Ziel verfügbar — dafür braucht es das Belegwesen oder die Geschäftspartner und das Leserecht darauf.</p>

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <select className="rounded-md border border-input bg-background px-2 py-1 text-sm" value={art} onChange={(e) => setArt(e.target.value)} aria-label="Ziel">
          <option value="">alle Ziele</option>
          {arten.map((a) => (
            <option key={a.kind} value={a.kind}>
              {a.label}
            </option>
          ))}
        </select>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <input
            className="w-full rounded-md border border-input bg-background py-1 pl-7 pr-2 text-sm"
            placeholder="Belegnummer, Partner, Projekt"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Ziel suchen"
          />
        </div>
      </div>
      {fehler && <div className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{fehler}</div>}
      {treffer && treffer.length === 0 && <p className="text-xs text-muted-foreground">{q ? 'Nichts gefunden.' : 'Keine Vorschläge — Belegnummer oder Partner suchen.'}</p>}
      <ul className="max-h-64 space-y-0.5 overflow-y-auto">
        {(treffer ?? []).map((z) => {
          const schon = besetzt.includes(z.targetKind + ':' + z.targetId)
          return (
            <li key={z.targetKind + z.targetId} className="flex items-center justify-between gap-2 rounded-md px-2 py-1 hover:bg-muted">
              <div className="min-w-0">
                <div className="truncate text-sm">{z.label}</div>
                <div className="truncate text-xs text-muted-foreground">{z.detail ?? artLabel(z.targetKind)}</div>
              </div>
              <button
                type="button"
                disabled={schon || !!laeuft}
                onClick={() => void waehle(z)}
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs hover:bg-background disabled:opacity-50"
              >
                {laeuft === z.targetKind + z.targetId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                {schon ? 'zugeordnet' : 'Zuordnen'}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
